import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { formatRupiah, formatDate, todayWITA } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import DatePicker from "@/components/DatePicker";
import { toast } from "sonner";
import { Plus, Trash2, Lock } from "lucide-react";

const tomorrow = () => { const d = new Date(`${todayWITA()}T12:00:00`); d.setDate(d.getDate() + 1); return d.toISOString().slice(0, 10); };
const blank = (amount = 0, date = tomorrow()) => ({ amount, method: "giro", cair_date: date, warkat_number: "", warkat_bank: "" });

export default function RestructureDialog({ schedule, onClose, onSaved }) {
  const cleared = schedule.is_split ? (schedule.tranches || []).filter((t) => t.cleared) : [];
  const remaining = Math.round(((schedule.net_amount || 0) - cleared.reduce((a, t) => a + t.amount, 0)) * 100) / 100;
  const defDate = schedule.payment_date > todayWITA() ? schedule.payment_date : tomorrow();
  const initial = schedule.is_split
    ? (schedule.tranches || []).filter((t) => !t.cleared).map((t) => ({ amount: t.amount, method: t.method, cair_date: t.cair_date, warkat_number: t.warkat_number || "", warkat_bank: t.warkat_bank || "" }))
    : [blank(Math.round(remaining / 2), defDate), blank(remaining - Math.round(remaining / 2), defDate)];
  const [rows, setRows] = useState(initial.length ? initial : [blank(remaining)]);
  const [reason, setReason] = useState("");
  const total = rows.reduce((a, r) => a + (Number(r.amount) || 0), 0);
  const diff = Math.round((remaining - total) * 100) / 100;
  const set = (i, k, v) => setRows(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));

  const submit = async () => {
    if (!reason) return toast.error("Isi alasan perubahan");
    if (Math.abs(diff) > 1) return toast.error("Total termin harus sama dengan sisa pembayaran");
    if (rows.some((r) => !r.cair_date || r.cair_date <= todayWITA())) return toast.error("Tanggal cair setiap termin harus setelah hari ini");
    try {
      await api.post("/change-requests", {
        type: "schedule_restructure", schedule_id: schedule.id, reason,
        new_data: { tranches: rows.map((r) => ({ ...r, amount: Number(r.amount), warkat_number: r.method === "giro" ? r.warkat_number : null, warkat_bank: r.method === "giro" ? r.warkat_bank : null })) },
      });
      toast.success("Pengajuan perubahan pembayaran dikirim, menunggu persetujuan");
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="restructure-dialog">
        <DialogHeader>
          <DialogTitle>Ubah / Split Pembayaran · {schedule.code}</DialogTitle>
          <DialogDescription>Bagi pembayaran menjadi beberapa termin (BG/transfer). Setiap termin nanti bisa direschedule sendiri.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="rounded-2xl bg-slate-50 p-3"><div className="text-xs text-slate-500">Sekarang</div><div className="font-semibold capitalize">{schedule.is_split ? `Split ${schedule.tranches.length} termin` : `${schedule.method} · ${formatDate(schedule.payment_date)}`}</div></div>
          <div className="rounded-2xl bg-slate-50 p-3"><div className="text-xs text-slate-500">Sisa yang dibagi</div><div className="font-bold" data-testid="restructure-remaining">{formatRupiah(remaining)}</div></div>
        </div>
        {cleared.length > 0 && (
          <div className="space-y-1">
            {cleared.map((t, i) => (
              <div key={t.id} className="flex items-center justify-between text-xs rounded-xl border border-emerald-100 bg-emerald-50 px-3 py-2"><span className="flex items-center gap-1.5"><Lock className="h-3 w-3" />Sudah cair · {formatDate(t.cair_date)}</span><span className="font-semibold">{formatRupiah(t.amount)}</span></div>
            ))}
          </div>
        )}
        <div className="space-y-3">
          {rows.map((r, i) => (
            <div key={i} className="rounded-2xl border border-slate-200 p-3 space-y-2" data-testid={`restructure-row-${i}`}>
              <div className="flex items-center justify-between"><span className="text-sm font-semibold">Termin {cleared.length + i + 1}</span>
                {rows.length > 1 && <Button type="button" size="icon" variant="ghost" onClick={() => setRows(rows.filter((_, j) => j !== i))} data-testid={`restructure-remove-${i}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                <div><Label className="text-[11px]">Nominal</Label><Input type="number" value={r.amount} onChange={(e) => set(i, "amount", e.target.value)} data-testid={`restructure-amount-${i}`} /></div>
                <div><Label className="text-[11px]">Metode</Label>
                  <Select value={r.method} onValueChange={(v) => set(i, "method", v)}>
                    <SelectTrigger data-testid={`restructure-method-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="giro">Bilyet Giro</SelectItem><SelectItem value="transfer">Transfer</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 sm:col-span-1"><Label className="text-[11px]">Tanggal Cair</Label><DatePicker value={r.cair_date} today={todayWITA()} minDate={tomorrow()} onChange={(v) => set(i, "cair_date", v)} testid={`restructure-date-${i}`} /></div>
              </div>
              {r.method === "giro" && (
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="No. Warkat" value={r.warkat_number} onChange={(e) => set(i, "warkat_number", e.target.value)} data-testid={`restructure-warkat-no-${i}`} />
                  <Input placeholder="Bank" value={r.warkat_bank} onChange={(e) => set(i, "warkat_bank", e.target.value)} data-testid={`restructure-warkat-bank-${i}`} />
                </div>
              )}
            </div>
          ))}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => setRows([...rows, blank(diff > 0 ? diff : 0)])} data-testid="restructure-add"><Plus className="h-4 w-4" />Tambah Termin</Button>
            <div className={`text-sm font-semibold ${Math.abs(diff) > 1 ? "text-rose-600" : "text-emerald-600"}`} data-testid="restructure-diff">
              {Math.abs(diff) > 1 ? `Selisih ${formatRupiah(diff)}` : "Total sesuai"}
            </div>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Alasan</Label><Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Contoh: Owner minta pembayaran dibagi 3 BG" data-testid="restructure-reason" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button onClick={submit} data-testid="restructure-submit">Ajukan Perubahan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
